<script lang="ts">
//
  import { createEventDispatcher, afterUpdate } from "svelte";
  import type { Chat, Message } from "../lib/types";

  export let chat: Chat;
  export let messages: Message[];
  export let hasMoreHistory = true;
  /**
   * Scroll intent set by the parent before mutating `messages`:
   *   "bottom"        — scroll to end unconditionally (initial load, chat switch)
   *   "bottom-if-near"— scroll to end only if user was already near the bottom
   *                     (live incoming message)
   *   "anchor"        — history prepended; restore visual position
   *   "none"          — do nothing (signature/member updates, etc.)
   */
  export let scrollBehavior: "bottom" | "bottom-if-near" | "anchor" | "none" = "bottom";

  const dispatch = createEventDispatcher();

  let messageInput = "";
  let messagesContainer: HTMLElement;
  let textarea: HTMLTextAreaElement;

  // Captured by the parent (via loadHistory dispatch → parent calls nothing;
  // we capture it ourselves just before the parent sets scrollBehavior="anchor").
  // We watch for scrollBehavior changing to "anchor" and capture at that moment.
  let savedScrollHeight = 0;
  let isNearBottom = true;
  const NEAR_BOTTOM_THRESHOLD = 120;

  afterUpdate(() => {
    if (!messagesContainer) return;
    if (scrollBehavior === "none") return;

    if (scrollBehavior === "bottom") {
      messagesContainer.scrollTop = messagesContainer.scrollHeight;
      isNearBottom = true;
    } else if (scrollBehavior === "bottom-if-near") {
      if (isNearBottom) {
        messagesContainer.scrollTop = messagesContainer.scrollHeight;
      }
    } else if (scrollBehavior === "anchor") {
      const delta = messagesContainer.scrollHeight - savedScrollHeight;
      messagesContainer.scrollTop += delta;
    }

    // Tell parent we've handled this scroll intent so it resets to "none"
    // and subsequent re-renders (signature updates etc.) don't re-scroll.
    dispatch("scrollConsumed");
  });

  const checkNearBottom = (): void => {
    if (!messagesContainer) return;
    const { scrollTop, scrollHeight, clientHeight } = messagesContainer;
    isNearBottom = scrollHeight - scrollTop - clientHeight <= NEAR_BOTTOM_THRESHOLD;
  };

  const loadHistory = (): void => {
    if (!hasMoreHistory) return;
    // Capture BEFORE the dispatch so parent can set scrollBehavior="anchor"
    // and afterUpdate will see the pre-prepend scrollHeight.
    if (messagesContainer) savedScrollHeight = messagesContainer.scrollHeight;
    dispatch("loadHistory", messages.length > 0 ? messages[0] : null);
  };

  const sendMessage = (): void => {
    if (!messageInput.trim()) return;
    dispatch("send", messageInput.trim());
    messageInput = "";
    if (textarea) textarea.style.height = "auto";
  };

  const handleKeydown = (e: KeyboardEvent): void => {
    if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleInput = (): void => {
    if (textarea) {
      textarea.style.height = "auto";
      textarea.style.height = Math.min(textarea.scrollHeight, 120) + "px";
    }
  };

  const formatDate = (dateStr: string): string =>
    new Date(dateStr).toISOString().split("T")[0];

  const currentUserUuid =
    typeof localStorage !== "undefined" ? localStorage.getItem("user_uuid") : null;
</script>

<div class="chat-messages">
  <div class="messages" bind:this={messagesContainer} on:scroll={checkNearBottom}>
    {#if hasMoreHistory}
      <button on:click={loadHistory} class="load-more">📜 Load older messages</button>
    {:else if messages.length > 0}
      <div class="no-more-history">No prior messages</div>
    {/if}

    {#each messages as message}
      {#if message.date_label}
        <div class="date-separator">
          <div class="date-pill">{formatDate(message.date_label)}</div>
        </div>
      {/if}
      <div
        class="message"
        class:own={message.is_own || message.user_uuid === currentUserUuid}
        data-message-id={message.id}
      >
        <div class="message-avatar">
          <img src={message.avatar_url} alt={message.username} />
        </div>
        <div class="message-content">
          <div class="message-header">
            <span class="username">{message.username}</span>
            <span class="timestamp">{message.timestamp}</span>
          </div>
          <div class="message-text">{message.content}</div>
        </div>
      </div>
    {/each}

    {#if messages.length === 0 && !hasMoreHistory}
      <div class="empty">No messages yet</div>
    {/if}
  </div>

  {#if chat.can_write}
    <div class="input-area">
      <textarea
        bind:this={textarea}
        bind:value={messageInput}
        on:keydown={handleKeydown}
        on:input={handleInput}
        placeholder="Type a message... (Ctrl+Enter to send)"
        rows="1"
      ></textarea>
      <button on:click={sendMessage} disabled={!messageInput.trim()}>Send</button>
    </div>
  {:else}
    <div class="read-only">You can only read messages in this chat</div>
  {/if}
</div>

<style>
.chat-messages { height: 100%; display: flex; flex-direction: column; background: #f8f9fa; }
.messages { flex: 1; overflow-y: auto; padding: 1rem; display: flex; flex-direction: column; gap: 0.75rem; }
.load-more {
  align-self: center; padding: 0.5rem 1rem; margin-bottom: 1rem;
  background: #ffffff; border: 2px solid #e9ecef; border-radius: 20px;
  cursor: pointer; font-size: 0.875rem; color: #495057;
  transition: all 0.2s; box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}
.load-more:hover { background: #f8f9fa; border-color: #ced4da; transform: translateY(-1px); box-shadow: 0 4px 8px rgba(0,0,0,0.15); }
.no-more-history {
  align-self: center; padding: 0.5rem 1rem; margin-bottom: 1rem;
  font-size: 0.875rem; color: #6c757d; font-style: italic;
  background: #e9ecef; border-radius: 20px;
}
.date-separator {
  display: flex; justify-content: center; margin: 1rem 0;
  position: sticky; top: 0; z-index: 1;
  background: #f8f9fa; padding: 0.5rem 0; box-shadow: 0 2px 5px rgba(0,0,0,0.05);
}
.date-pill { background: #6c757d; color: white; padding: 0.25rem 0.75rem; border-radius: 12px; font-size: 0.75rem; font-weight: 500; }
.message { display: flex; gap: 0.75rem; max-width: 75%; }
.message.own { flex-direction: row-reverse; align-self: flex-end; }
.message-avatar { flex-shrink: 0; }
.message-avatar img { width: 36px; height: 36px; border-radius: 50%; border: 2px solid #ffffff; box-shadow: 0 2px 4px rgba(0,0,0,0.1); }
.message-content { flex: 1; min-width: 0; }
.message-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.25rem; gap: 0.5rem; }
.message.own .message-header { flex-direction: row-reverse; }
.username { font-weight: 600; font-size: 0.875rem; color: #495057; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.timestamp { font-size: 0.75rem; color: #6c757d; white-space: nowrap; }
.message-text { background: #ffffff; padding: 0.75rem 1rem; border-radius: 18px; white-space: pre-wrap; word-wrap: break-word; line-height: 1.4; color: #212529; box-shadow: 0 2px 4px rgba(0,0,0,0.1); border: 1px solid #e9ecef; }
.message.own .message-text { background: #007bff; color: white; border-color: #007bff; }
.empty { text-align: center; color: #6c757d; margin-top: 2rem; font-style: italic; }
.input-area { display: flex; padding: 1rem; background: white; border-top: 1px solid #e9ecef; gap: 0.75rem; }
textarea { flex: 1; border: 2px solid #e9ecef; border-radius: 20px; padding: 0.75rem 1rem; resize: none; max-height: 120px; min-height: 44px; font-family: inherit; line-height: 1.4; font-size: 0.95rem; transition: border-color 0.2s; }
textarea:focus { outline: none; border-color: #007bff; }
button { padding: 0.75rem 1.5rem; background: #007bff; color: white; border: none; border-radius: 20px; cursor: pointer; align-self: flex-end; font-weight: 500; transition: all 0.2s; box-shadow: 0 2px 4px rgba(0,123,255,0.3); }
button:hover:not(:disabled) { background: #0056b3; transform: translateY(-1px); box-shadow: 0 4px 8px rgba(0,123,255,0.4); }
button:disabled { background: #6c757d; cursor: not-allowed; transform: none; box-shadow: none; }
.read-only { padding: 1rem; text-align: center; background: #f8f9fa; color: #6c757d; border-top: 1px solid #e9ecef; font-style: italic; }
</style>
